import { useEffect, useState } from "react";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { FastViLogo } from "../components/FastViLogo";
import { Download, Printer } from "lucide-react";

const SECTIONS = [
  { id: "intro", label: "1. Introduction" },
  { id: "roles", label: "2. Roles & access" },
  { id: "command-center", label: "3. Super Admin Command Center" },
  { id: "buildings", label: "4. Buildings & units" },
  { id: "people", label: "5. Users, guards & passes" },
  { id: "visitor-qr", label: "6. Visitor QR doorbell" },
  { id: "doors", label: "7. Door relay control" },
  { id: "push", label: "8. Push notifications" },
  { id: "password-reset", label: "9. Password reset" },
  { id: "mobile", label: "10. Mobile app" },
  { id: "security", label: "11. Security model" },
  { id: "activity", label: "12. Activity audit" },
  { id: "runbook", label: "13. Runbook & troubleshooting" },
];

export default function ManualPage() {
  const [active, setActive] = useState("intro");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  function downloadPdf() {
    // Uses the browser print dialog with print styles — users pick "Save as PDF".
    window.print();
  }

  return (
    <Layout>
      <style>{`
        @media print {
          /* Hide app chrome */
          aside, .md\\:hidden, .no-print { display: none !important; }
          main { padding: 0 !important; }
          body { background: white !important; color: black !important; }
          .manual-shell { max-width: none !important; padding: 24px !important; grid-template-columns: 1fr !important; gap: 0 !important; }
          .manual-toc { display: none !important; }
          .manual-section { break-inside: avoid; page-break-inside: avoid; margin-top: 24px; }
          .manual-section + .manual-section { page-break-before: auto; }
          h1, h2, h3 { color: black !important; }
          a { color: black !important; text-decoration: none !important; }
          .card-stroke { border: 1px solid #ccc !important; box-shadow: none !important; }
          .fastvi-print-header { display: flex !important; page-break-after: always; }
        }
        .fastvi-print-header { display: none; }
      `}</style>

      <div className="fastvi-print-header items-center gap-4 border-b border-border pb-4 mb-6 flex-col text-center">
        <FastViLogo size={120}/>
        <div className="mt-3">
          <div className="font-display text-4xl">FastVi — Platform Manual</div>
          <div className="text-sm text-muted-foreground mt-2">Super Admin operator's guide</div>
          <div className="text-xs font-mono text-muted-foreground mt-6">Printed {new Date().toLocaleString()}</div>
        </div>
      </div>

      <header className="mb-8 fade-up flex items-start justify-between flex-wrap gap-4 no-print">
        <div>
          <div className="label-tiny">Documentation</div>
          <h1 className="font-display text-4xl">User Manual</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            The complete operator's guide for FastVi super admins — from creating
            buildings and printing unit QRs to the hardware door relay and the
            mobile apps.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadPdf} data-testid="manual-print">
            <Printer size={14} className="mr-1.5"/> Print
          </Button>
          <Button onClick={downloadPdf} data-testid="manual-download-pdf">
            <Download size={14} className="mr-1.5"/> Download as PDF
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-10 manual-shell">
        <aside className="manual-toc no-print lg:sticky lg:top-6 self-start">
          <div className="label-tiny mb-3">Contents</div>
          <nav className="flex flex-col gap-1 text-sm" data-testid="manual-toc">
            {SECTIONS.map((s) => (
              <a key={s.id} href={`#${s.id}`}
                 data-testid={`manual-toc-${s.id}`}
                 className={`px-2 py-1 rounded transition-colors ${
                   active === s.id ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground hover:text-foreground"
                 }`}>
                {s.label}
              </a>
            ))}
          </nav>
        </aside>

        <article className="space-y-12 leading-7" data-testid="manual-article">
          {/* 1. INTRODUCTION */}
          <section id="intro" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">1. Introduction</h2>
            <p>
              FastVi is a secure video intercom and visitor-pass system for
              residential buildings. A single platform serves five surfaces: a
              Visitor Kiosk at the lobby (also reached by scanning a printed
              unit QR), a Resident app (web + mobile) that rings on video calls
              and unlocks doors, a Guard dashboard (web + mobile) for live
              monitoring and QR scanning, a Building Admin dashboard, and this
              Super Admin Command Center where the fleet is managed.
            </p>
            <p className="mt-3">
              This manual walks a super admin through every tool exposed under
              <span className="font-mono"> /admin</span>, the mobile apps, and
              the operational playbook you'll reach for when something misbehaves.
            </p>
          </section>

          {/* 2. ROLES & ACCESS */}
          <section id="roles" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">2. Roles & access</h2>
            <div className="card-stroke p-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="label-tiny text-left border-b border-border">
                    <th className="py-2 pr-4">Role</th>
                    <th className="pr-4">Public self-registration</th>
                    <th className="pr-4">Super-admin creatable</th>
                    <th>What they see</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4 font-mono">resident</td><td className="pr-4">✅</td><td className="pr-4">✅</td>
                    <td>Own calls, own passes, own door events; unit-scoped.</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4 font-mono">guard</td><td className="pr-4">✅</td><td className="pr-4">✅</td>
                    <td>Live calls + passes for their assigned building(s).</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4 font-mono">admin</td><td className="pr-4">❌</td><td className="pr-4">✅</td>
                    <td>Everything inside the building they manage.</td>
                  </tr>
                  <tr className="border-b border-border">
                    <td className="py-2 pr-4 font-mono">super_admin</td><td className="pr-4">❌ (bootstrap)</td><td className="pr-4">❌</td>
                    <td>All buildings. Can impersonate any role route.</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-mono">visitor</td><td className="pr-4">❌</td><td className="pr-4">✅</td>
                    <td>Internal role only — visitors use the kiosk / QR, not an account.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4">
              <strong>Super-admin full bypass:</strong> a super admin can visit
              <span className="font-mono"> /resident</span>,
              <span className="font-mono"> /guard</span> and
              <span className="font-mono"> /building-admin</span> directly for
              support &amp; debugging — <span className="font-mono">ProtectedRoute</span>
              lets them through every role gate.
            </p>
          </section>

          {/* 3. COMMAND CENTER */}
          <section id="command-center" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">3. Super Admin Command Center</h2>
            <p>
              Navigate to <span className="font-mono">/admin</span>. The header
              strip shows live counters (users, buildings, units, guards, active
              passes, calls all-time) that refresh on demand. Below it, seven
              tabs expose every operational control:
            </p>
            <ol className="list-decimal pl-6 mt-4 space-y-3">
              <li>
                <strong>Users</strong> — filter by free-text / role / building,
                create accounts of any creatable role, edit (name, email, phone,
                password, role, building, unit assignment), toggle active status,
                <strong> Force logout</strong> (bumps
                <span className="font-mono"> session_version</span>, invalidating
                every live token immediately), delete. The bootstrap super admin
                is protected from deletion.
              </li>
              <li>
                <strong>Buildings</strong> — create / rename / change code /
                reassign admin. <strong>Delete cascades</strong> units, passes,
                calls, activity, door controller and users whose only building
                is this one. The confirm dialog requires retyping the exact
                building code.
              </li>
              <li>
                <strong>Units</strong> — pick a building to list its units,
                create / rename / delete, assign &amp; unassign residents.
                Each row has a <strong>QR</strong> action that opens a printable
                unit-QR modal (see §6).
              </li>
              <li>
                <strong>Guards</strong> — list, activate / deactivate (switch),
                create / edit with primary building + multi-building coverage
                checklist, delete.
              </li>
              <li>
                <strong>Passes</strong> — platform-wide list with filters
                (free-text, status, building). Issue a pass <em>on behalf of</em>
                any resident, revoke an active pass (writes a
                <span className="font-mono"> pass_revoked</span> activity row
                attributed to you).
              </li>
              <li>
                <strong>Doors</strong> — one card per building. Edit the adapter,
                Test connection, remote <strong>Unlock door</strong> with
                confirmation (see §7). Removing a controller is also here.
              </li>
              <li>
                <strong>Activity</strong> — server-side filters by building,
                user, event type, date-from / date-to, with Prev/Next pagination.
              </li>
            </ol>
          </section>

          {/* 4. BUILDINGS & UNITS */}
          <section id="buildings" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">4. Buildings & units</h2>
            <p>
              A <strong>building</strong> is the tenancy boundary — every
              resident, guard, pass, call, controller and activity row lives
              inside one. Each building has a short uppercase code (e.g.
              <span className="font-mono"> FASTVI</span>) that visitors type at
              the kiosk and residents paste during registration.
            </p>
            <p className="mt-3">
              A <strong>unit</strong> belongs to one building and holds one or
              more residents. You can reassign a resident from either the Users
              tab (unit field) or the Units tab (assign action). Residents
              removed from a unit stay in the system without a unit until
              reassigned.
            </p>
            <p className="mt-3">
              <strong>Cascade delete:</strong> deleting a building purges all of
              its units, passes, calls, activity, door controller, pending door
              commands AND any user whose only membership is this building
              (super admins excluded). The dialog forces you to retype the
              building code for safety.
            </p>
          </section>

          {/* 5. PEOPLE */}
          <section id="people" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">5. Users, guards & passes</h2>
            <p>
              The Users tab is the flat list of every account on FastVi. Row
              actions:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-1">
              <li><strong>Edit</strong> — name, email, phone, password, role, building, unit.</li>
              <li><strong>Force logout</strong> — bumps <span className="font-mono">session_version</span>, kills every live token and WS. Also writes a <span className="font-mono">force_logout</span> activity row.</li>
              <li><strong>Activate / deactivate</strong> — click the status pill. Deactivated accounts can't sign in.</li>
              <li><strong>Delete</strong> — permanent. The bootstrap super admin is protected.</li>
            </ul>
            <p className="mt-4">
              Guards get a dedicated tab because they often cover multiple
              buildings. The guard edit modal exposes a multi-building checklist
              in addition to the primary building.
            </p>
            <p className="mt-4">
              Residents issue passes themselves from the mobile app or the web
              resident dashboard, but a super admin can issue one{" "}
              <strong>on behalf of</strong> any resident from the Passes tab —
              handy when a resident calls the office panicked that a delivery
              just arrived. Each pass has a 12-character code, a validity window
              (hours), and status flow
              <span className="font-mono"> active → used | revoked | expired</span>.
            </p>
          </section>

          {/* 6. VISITOR QR DOORBELL */}
          <section id="visitor-qr" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">6. Visitor QR doorbell (no signup)</h2>
            <p>
              Visitors do <strong>not</strong> sign up. They either walk up to
              the lobby kiosk and type the building code, or they scan a printed
              unit QR with any phone camera. The QR encodes a kiosk deep link:
            </p>
            <pre className="card-stroke bg-muted/40 p-3 rounded-md text-xs font-mono mt-3 overflow-x-auto">
{"https://<frontend-host>/kiosk?building=FASTVI&unit=101"}
            </pre>
            <p className="mt-3">
              Scanning opens the Visitor Kiosk already past the building-code
              step with the correct unit preselected on the visitor-details
              screen. One tap → <span className="font-mono">POST /api/calls/initiate</span>
              rings the unit.
            </p>
            <p className="mt-3"><strong>Printing unit QRs:</strong></p>
            <ol className="list-decimal pl-6 mt-2 space-y-1">
              <li>Super Admin → <span className="font-mono">/admin</span> → Units tab (or Building Admin's Units table).</li>
              <li>Pick the building, click the <strong>QR icon</strong> on a unit row.</li>
              <li>A modal renders a big QR encoding the deep link, plus the raw URL. Use <strong>Print</strong> for a clean single-QR sheet (FastVi branded) or <strong>Copy link</strong> to paste it into email / an existing door-sign template.</li>
            </ol>
            <p className="mt-3">
              Invalid building codes in a scanned link don't crash the kiosk —
              it falls back to the building-code entry with an inline
              "Building not found" error.
            </p>
          </section>

          {/* 7. DOOR RELAY CONTROL */}
          <section id="doors" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">7. Door relay control</h2>
            <p>
              Each building can wire up a door controller from the Doors tab.
              Two adapters ship with FastVi:
            </p>
            <div className="space-y-3 mt-4">
              <div className="card-stroke p-4">
                <div className="font-medium mb-1">HTTP direct</div>
                <p className="text-sm">
                  FastVi POSTs (or GETs) a signed pulse request straight to a
                  URL you own — a Shelly / Sonoff relay, a custom controller.
                  Every request carries HMAC-SHA256 headers:
                </p>
                <pre className="bg-muted/40 p-2 rounded text-xs font-mono mt-2 overflow-x-auto">
{"X-FastVi-Timestamp: 2026-10-02T20:15:10.397Z"}
{"\nX-FastVi-Signature: <HMAC-SHA256 of timestamp|call_id|building_id>"}
{"\nX-FastVi-Call-Id:   <call-or-admin-id>"}
{"\nX-FastVi-Building-Id: <building_id>"}
                </pre>
                <p className="text-sm mt-2">
                  The signing secret is either the controller's{" "}
                  <span className="font-mono">auth_token</span> (if you set one)
                  or an HMAC derived from <span className="font-mono">JWT_SECRET</span>.
                  Verify the signature on the device side.
                </p>
              </div>
              <div className="card-stroke p-4">
                <div className="font-medium mb-1">Gateway poller</div>
                <p className="text-sm">
                  An on-prem device long-polls{" "}
                  <span className="font-mono">GET /api/gateway/commands?building_id=…</span>{" "}
                  with header <span className="font-mono">X-Gateway-Key</span>.
                  Commands expire after <strong>60 seconds</strong> — anything
                  older than that is auto-marked expired. Rotate the key from
                  the controller card to invalidate the previous poller
                  immediately.
                </p>
              </div>
            </div>
            <p className="mt-4">
              <strong>Test connection</strong> fires a fake pulse so you can
              verify wiring without ringing anyone.{" "}
              <strong>Unlock door</strong> (from the admin Doors tab) fires a
              real pulse, writes a <span className="font-mono">door_unlock</span>{" "}
              activity row with <span className="font-mono">source: "admin_panel"</span>{" "}
              attributed to you, and surfaces delivery status.
            </p>
            <p className="mt-4">
              <strong>Delivery statuses</strong> (shown on the controller card
              and recorded on every activity row):
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1 text-sm">
              <li><span className="font-mono">sent</span> — HTTP 2xx came back from the relay URL.</li>
              <li><span className="font-mono">acked</span> — gateway poller ack'ed the command.</li>
              <li><span className="font-mono">pending</span> — queued for a gateway poller that hasn't picked it up yet.</li>
              <li><span className="font-mono">failed</span> — relay returned non-2xx, or request raised.</li>
              <li><span className="font-mono">expired</span> — gateway didn't ack within 60s.</li>
              <li><span className="font-mono">no_controller</span> — building has nothing configured.</li>
            </ul>
            <p className="mt-4">
              <strong>This environment ships a mock relay</strong> at{" "}
              <span className="font-mono">POST /api/mock/door/{"{building_id}"}</span>.
              The seed script auto-wires the demo <span className="font-mono">FASTVI</span>{" "}
              building to it with a random HMAC secret so the golden path
              (kiosk → ring → accept → Unlock) just works out of the box.
              Replace it with a real controller URL via the Doors tab before
              taking the building to production.
            </p>
          </section>

          {/* 8. PUSH NOTIFICATIONS */}
          <section id="push" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">8. Push notifications</h2>
            <p>
              Residents on the web app can opt into VAPID Web Push so their
              browser rings even when the FastVi tab is closed.
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-1">
              <li>
                A yellow banner on the Resident Dashboard offers "Enable
                notifications" the first time a resident lands there.
              </li>
              <li>
                Clicking Enable asks the browser for permission → registers a
                service worker → POSTs the subscription to{" "}
                <span className="font-mono">/api/push/subscribe</span>.
              </li>
              <li>
                A <strong>Test</strong> button inside Settings fires a push via
                <span className="font-mono"> /api/push/test</span> so residents
                can verify delivery before the first real call.
              </li>
              <li>
                When a visitor rings a unit, FastVi fans out a push to every
                subscribed resident of that unit alongside the WebSocket{" "}
                <span className="font-mono">incoming_call</span> message.
              </li>
              <li>
                Expired / revoked endpoints (HTTP 404/410) are auto-pruned
                from <span className="font-mono">db.push_subscriptions</span>.
              </li>
            </ul>
            <p className="mt-4"><strong>Browser caveats:</strong></p>
            <ul className="list-disc pl-6 mt-2 space-y-1 text-sm">
              <li>Chrome / Edge / Firefox / Opera on desktop and Android — full support.</li>
              <li><strong>iOS Safari requires 16.4+ AND the site to be installed as a PWA</strong> ("Add to Home Screen"). Browser-tab push on iOS is still not supported.</li>
              <li>macOS Safari works since 16.
                User must grant permission from the FastVi tab.
              </li>
            </ul>
          </section>

          {/* 9. PASSWORD RESET */}
          <section id="password-reset" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">9. Password reset</h2>
            <p>Flow:</p>
            <ol className="list-decimal pl-6 mt-2 space-y-1">
              <li>User clicks <strong>Forgot password?</strong> on /login → <span className="font-mono">/forgot-password</span> → enters email.</li>
              <li><span className="font-mono">POST /api/auth/forgot-password</span> always returns 200 with a generic message (no user enumeration). If the email matches an account, FastVi stores a SHA-256-hashed single-use token (TTL <strong>30 minutes</strong>) in <span className="font-mono">db.password_resets</span> and invalidates any previous unused tokens for that user — only one active token per user at a time.</li>
              <li>Reset link format: <span className="font-mono">{"{FRONTEND_URL}/reset-password?token=…"}</span>.</li>
              <li>User opens the link, picks a new password (≥ 8 chars), confirms → <span className="font-mono">POST /api/auth/reset-password</span>.</li>
              <li>FastVi updates <span className="font-mono">password_hash</span>, bumps <span className="font-mono">session_version</span> (same mechanism as Force logout — <strong>every live session dies</strong>), marks the token used, deletes every other outstanding token for that user, writes a <span className="font-mono">password_reset</span> activity row.</li>
            </ol>
            <p className="mt-4"><strong>Email delivery:</strong></p>
            <div className="card-stroke p-4 mt-2 text-sm">
              <p className="font-medium">Mock mode (current default on this deployment)</p>
              <p className="mt-1">
                When <span className="font-mono">SMTP_HOST</span> + <span className="font-mono">SMTP_FROM</span> are not configured, the mailer logs the reset link to <span className="font-mono">backend.err.log</span> with a <span className="font-mono">[MOCK-EMAIL]</span> prefix and includes <span className="font-mono">dev_reset_link</span> in the API response. The web UI does NOT display this field — it's for ops / automation.
              </p>
              <pre className="bg-muted/40 p-2 rounded text-xs font-mono mt-2 overflow-x-auto">
tail -f /var/log/supervisor/backend.err.log | grep MOCK-EMAIL
              </pre>
            </div>
            <div className="card-stroke p-4 mt-3 text-sm">
              <p className="font-medium">Switching to real SMTP</p>
              <p className="mt-1">Set these in <span className="font-mono">/app/backend/.env</span> and restart the backend:</p>
              <pre className="bg-muted/40 p-2 rounded text-xs font-mono mt-2 overflow-x-auto">
{"SMTP_HOST=\"smtp.yourprovider.com\""}
{"\nSMTP_PORT=\"587\""}
{"\nSMTP_USER=\"apikey-or-login\""}
{"\nSMTP_PASS=\"secret\""}
{"\nSMTP_FROM=\"FastVi <no-reply@yourdomain.com>\""}
              </pre>
              <p className="mt-2">
                With both <span className="font-mono">SMTP_HOST</span> and <span className="font-mono">SMTP_FROM</span> set, FastVi sends via <span className="font-mono">smtplib</span> (STARTTLS, auth if user/pass set) on a worker thread. The <span className="font-mono">dev_reset_link</span> field disappears from responses. Full details in <span className="font-mono">/app/memory/RUNBOOK.md</span> → <em>Password reset mailer</em>.
              </p>
            </div>
          </section>

          {/* 10. MOBILE APP */}
          <section id="mobile" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">10. Mobile app</h2>
            <p>
              FastVi ships a native Expo app alongside the web surfaces. Preview
              URL for the current deployment lives in{" "}
              <span className="font-mono">/app/memory/test_credentials.md</span>.
              The <strong>FastVi branded splash</strong> animates the logo on
              every boot.
            </p>
            <p className="mt-3"><strong>Registration — mobile:</strong></p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Only <span className="font-mono">resident</span> and <span className="font-mono">guard</span> are publicly registrable (same policy as web).</li>
              <li>Admins are provisioned by a super admin through the Users tab.</li>
              <li>Visitors do not register — they use the kiosk / scan a unit QR (see §6).</li>
            </ul>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="card-stroke p-4">
                <div className="font-medium mb-1">Resident app</div>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  <li><strong>Home</strong>: unit + building snapshot, WS live indicator, recent call history.</li>
                  <li><strong>Incoming call</strong>: full-screen ring UI with visitor name &amp; photo → accept / reject.</li>
                  <li><strong>Live video call</strong>: WebRTC peer with local PiP + remote tile, mute / camera / Unlock / End controls.</li>
                  <li><strong>Unlock</strong>: during an accepted call, tap Unlock → <span className="font-mono">POST /api/calls/{"{id}"}/unlock</span> → delivery status toast.</li>
                  <li><strong>Passes</strong>: create QR passes, list active/past, scannable detail view.</li>
                  <li><strong>Settings</strong>: user info, backend URL, WS status, version, logout.</li>
                </ul>
              </div>
              <div className="card-stroke p-4">
                <div className="font-medium mb-1">Guard app</div>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  <li><strong>Guard Home</strong>: today's stats (passes validated, door unlocks, calls accepted), activity feed, WS live indicator.</li>
                  <li><strong>Scanner</strong>: manual code entry + <strong>expo-camera QR scan</strong> → <span className="font-mono">POST /api/passes/validate</span>.</li>
                  <li>Valid pass → green "Access Granted" with visitor name, issuer, code, expiry.</li>
                  <li>Same code again → red "Already Used". Garbage code → red "Pass Not Found".</li>
                  <li><strong>Settings</strong>: identical surface to the resident app (dark theme).</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 11. SECURITY MODEL */}
          <section id="security" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">11. Security model</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Passwords are stored bcrypt-hashed. Password reset enforces an 8-character minimum.</li>
              <li>
                Sessions use short-lived HTTP-only JWT cookies. Every token carries a <span className="font-mono">session_version</span> (<span className="font-mono">sv</span>) claim. Bumping the user's <span className="font-mono">session_version</span> in Mongo instantly invalidates every live token on every device — this is what powers both <strong>Force logout</strong> and <strong>Password reset</strong>.
              </li>
              <li>Deactivated accounts (<span className="font-mono">is_active: false</span>) can't log in and are rejected mid-session on their next request.</li>
              <li>Role matrix: <span className="font-mono">resident</span>, <span className="font-mono">guard</span>, <span className="font-mono">admin</span>, <span className="font-mono">super_admin</span>, <span className="font-mono">visitor</span> (internal only). See §2 for who can self-register vs who the super admin provisions.</li>
              <li>Super-admin full bypass is enforced server-side in <span className="font-mono">require_role</span> AND client-side in <span className="font-mono">ProtectedRoute</span>, so support can impersonate every surface without secondary credentials.</li>
              <li>Door-unlock requests to on-prem hardware are HMAC-SHA256 signed per building (§7).</li>
              <li>Password-reset tokens are stored SHA-256 hashed — a DB leak doesn't expose live reset links. Tokens are one-time-use, 30-minute TTL, auto-replaced on new request.</li>
              <li>Visitor photos captured at the kiosk live inside the call document for the lifetime of the call; purged on building deletion.</li>
              <li>TLS end-to-end between browser and backend. WebRTC media flows peer-to-peer (TURN only when direct fails) and is <strong>not recorded</strong>.</li>
            </ul>
            <p className="mt-4"><strong>Audit event types written to <span className="font-mono">db.activity</span>:</strong></p>
            <div className="card-stroke p-4 mt-2 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 font-mono text-xs">
                <div>login</div>
                <div>force_logout</div>
                <div>password_reset</div>
                <div>call_ringing</div>
                <div>call_accepted</div>
                <div>call_rejected</div>
                <div>call_ended</div>
                <div>call_no_answer</div>
                <div>qr_entry</div>
                <div>pass_revoked</div>
                <div>door_unlock *</div>
              </div>
              <div className="text-xs text-muted-foreground mt-3">
                * <span className="font-mono">door_unlock</span> carries a{" "}
                <span className="font-mono">source</span> field —{" "}
                <span className="font-mono">admin_panel</span> for super-admin
                remote unlocks, resident-id otherwise — plus a{" "}
                <span className="font-mono">delivery</span> object with the
                adapter outcome (<span className="font-mono">sent</span>,{" "}
                <span className="font-mono">acked</span>,{" "}
                <span className="font-mono">failed</span>, etc.).
              </div>
            </div>
          </section>

          {/* 12. ACTIVITY */}
          <section id="activity" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">12. Activity audit</h2>
            <p>
              Every security-relevant event is appended to{" "}
              <span className="font-mono">db.activity</span> with the full set
              of types listed in §11. The Activity tab exposes server-side
              filtering by building, user (actor / resident / guard / user), event
              type, and <strong>date-from / date-to</strong> range, with
              Prev / Next pagination (50 rows / page).
            </p>
            <p className="mt-3">
              Residents see their unit's activity. Guards and building admins
              see their building's activity. Super admins see everything via
              <span className="font-mono"> /api/platform/activity</span>.
            </p>
          </section>

          {/* 13. RUNBOOK */}
          <section id="runbook" className="manual-section scroll-mt-24">
            <h2 className="font-display text-3xl mb-3">13. Runbook & troubleshooting</h2>
            <div className="space-y-4">
              <div className="card-stroke p-4">
                <div className="font-medium mb-1">A resident says their phone never rings</div>
                <ol className="list-decimal pl-5 text-sm space-y-1">
                  <li>Check <strong>Online right now</strong> on the stats strip — their WS must be open for in-tab ringing.</li>
                  <li>Push: open their resident account on web, re-enable the push banner. Chrome and iOS Safari revoke permissions silently more often than you'd expect.</li>
                  <li>Filter the Activity tab by their user → look for <span className="font-mono">call_ringing</span>. If it's missing, the kiosk never managed to initiate.</li>
                </ol>
              </div>
              <div className="card-stroke p-4">
                <div className="font-medium mb-1">The door controller won't fire</div>
                <ol className="list-decimal pl-5 text-sm space-y-1">
                  <li>Open the controller card for the building → <strong>Test connection</strong>.</li>
                  <li>If HTTP: verify the URL is reachable from the FastVi backend's egress and the <span className="font-mono">auth_token</span> matches on the device.</li>
                  <li>If gateway: rotate the key, restart the on-prem poller, re-test.</li>
                  <li>Still stuck? Grab the latest <span className="font-mono">door_unlock</span> activity rows and inspect <span className="font-mono">delivery.detail</span>.</li>
                </ol>
              </div>
              <div className="card-stroke p-4">
                <div className="font-medium mb-1">Password-reset email never arrives</div>
                <ol className="list-decimal pl-5 text-sm space-y-1">
                  <li>On this environment the mailer is in <strong>mock mode</strong> — the link only hits the backend log. Grep <span className="font-mono">MOCK-EMAIL</span> in <span className="font-mono">backend.err.log</span> and give the user the link manually, or wire up SMTP (§9).</li>
                  <li>With SMTP configured and no delivery, check backend logs for <span className="font-mono">"SMTP send failed"</span> — the user still gets a generic 200 and can retry.</li>
                </ol>
              </div>
              <div className="card-stroke p-4">
                <div className="font-medium mb-1">A compromised admin account</div>
                <ol className="list-decimal pl-5 text-sm space-y-1">
                  <li><strong>Force logout</strong> the user to invalidate every live token.</li>
                  <li><strong>Deactivate</strong> the account to block re-login.</li>
                  <li>Reset the password from the edit modal if you want to keep the account alive.</li>
                  <li>Audit the Activity tab for <span className="font-mono">door_unlock</span> rows with <span className="font-mono">source: "admin_panel"</span> and <span className="font-mono">pass_revoked</span> entries attributed to that user.</li>
                </ol>
              </div>
            </div>
          </section>
        </article>
      </div>
    </Layout>
  );
}
